import { createSelector, createStructuredSelector } from 'reselect';
import _ from 'lodash';
import { List, fromJS } from 'immutable';
import { overhangInvalidSelector, kitchenIslandOverhangInvalidSelector } from 'domain/Options/selectors/overhangMeasurements';
import { freestandingHobInvalidSelector } from 'pages/MainPage/CutoutsPage/selectors';
import { WALL_PANEL_ID } from 'domain/Options/generators/splashbacks';
import { shapeInvalidSelector, wallPanelHeightInvalidSelector, wallEdgingStripCustomizableHeightInvalidSelector, kitchenIslandInvalidSelector } from './errors';
import { allOperationParameters } from './operations';
import { planSelector, wallPanelMatchWorktopSelector, configurationSelector } from '../selectors';
import * as parameters from './parameters';
import * as availableOptions from './availableOptions';
import parameterMessages from '../parameterMessages';
import * as constants from '../constants';
import { SELECTION_TYPE_USER, SELECTION_TYPE_AUTOMATIC, SELECTION_TYPE_DEFAULT } from '../selectionTypes';
import createFieldSelector from './utils/createFieldSelector';
import conflictCategories from '../conflictCategories';

const addAutomatic = (lastAction, action, message, parameter, category) => {
  if (parameter.get('selectionType') === SELECTION_TYPE_AUTOMATIC) {
    return { message, lastAction: lastAction === action, category };
  }

  return null;
};

const addConflicts = (plan, configuration, lastAction, action, message, option, autoResolve, category) => {
  if (!option || (option.get('conflicts') && option.get('conflicts').size > 0)) {
    if (plan.getIn(['material', 'value', 'id']) === 'noMaterial' && (action === 'app/Options/SELECT_EXPRESSION' || action === "app/Options/SELECT_THICKNESS" || action === "app/Options/SELECT_COVER_PANEL")) {
      return null;
    }
    if (plan.getIn(['material', 'value', 'id']) !== 'noMaterial' && action === "app/Options/SELECT_COVER_PANEL") {
      return null;
    }
    if (configuration.get('noWallPanelMaterials') && (action === 'app/Options/SELECT_KITCHEN_ISLAND_SPLASH_BACK' || action === 'app/Options/SELECT_SPLASHBACK' || action === 'app/Options/SELECT_WALL_PANEL_MATERIAL' || action === 'app/Options/SELECT_WALL_PANEL_EXPRESSION')) {
      return null;
    } if (!configuration.get('useCoverPanels') && (action === 'app/Options/SELECT_COVER_PANEL')) {
      return null;
    } if (plan.getIn(['material', 'value', 'edges']) === 'noEdgeIncluded' && (action === 'app/Options/SELECT_EDGE') || plan.getIn(['kitchenIslandMaterial', 'value', 'edges'])  === 'noEdgeIncluded' && (action === 'app/Options/SELECT_KITCHEN_ISLAND_EDGE')) {
      return null;
    } if (option === undefined && category === 'kitchen-island') { // when a wood worktop is loaded from VPC, and another wood worktop expression is selected, a bug occurs from the kitchen island expression, where the option appears to be undefined. 
      // a conflict should not occur in this combination, so a custom solution is added to return null (no conflict) if this happens.
      return null;
    } else {
      // Ignore prevents this from showing ConflictDialog
      return { message, lastAction: option && lastAction === action, ignore: !option, autoResolve, category };
    }
  }
  return null;
};

export const selectedFromAvailableSelector = (selectedSelector, availableSelector) => {
  return createSelector(selectedSelector, availableSelector, (selected, available) => {
    return available.find((o) => o.get('id') === selected.getIn(['value', 'id']));
  });
};

const parameterWithConflictHandling = {
  material: {
    actionType: constants.SELECT_MATERIAL,
    message: parameterMessages.material,
    selected: parameters.materialParameterSelector,
    available: availableOptions.availableMaterialsSelector,
    category: conflictCategories.style,
  },
  expression: {
    actionType: constants.SELECT_EXPRESSION,
    message: parameterMessages.expression,
    selected: parameters.expressionParameterSelector,
    available: availableOptions.availableExpressionsSelector,
    category: conflictCategories.style,
  },
  coverpanel: {
    actionType: constants.SELECT_COVER_PANEL,
    message: parameterMessages.coverpanel,
    selected: parameters.coverPanelParameterSelector,
    available: availableOptions.availableCoverPanelsSelector,
    category: conflictCategories.style,
  },
  edge: {
    actionType: constants.SELECT_EDGE,
    message: parameterMessages.edge,
    selected: parameters.edgeParameterSelector,
    available: availableOptions.availableEdgesSelector,
    autoResolve: true,
    category: conflictCategories.style,
  },
  thickness: {
    actionType: constants.SELECT_THICKNESS,
    message: parameterMessages.thickness,
    selected: parameters.thicknessParameterSelector,
    available: availableOptions.availableThicknessesSelector,
    autoResolve: true,
    category: conflictCategories.style,
  },
  sinkModel: {
    actionType: constants.SELECT_SINK_MODEL,
    message: parameterMessages.sinkModel,
    selected: parameters.sinkModelParameterSelector,
    available: availableOptions.availableSinkModelsSelector,
    category: conflictCategories.sink,
  },
  sinkFastening: {
    actionType: constants.SELECT_SINK_FASTENING,
    message: parameterMessages.sinkFastening,
    selected: parameters.sinkFasteningParameterSelector,
    available: availableOptions.availableSinkFasteningsSelector,
    autoResolve: true,
    category: conflictCategories.sink,
  },
  drainerGrooves: {
    actionType: constants.SELECT_DRAINER_GROOVES,
    message: parameterMessages.drainerGrooves,
    selected: parameters.drainerGroovesParameterSelector,
    available: availableOptions.availableDrainerGroovesSelector,
    autoResolve: true,
    category: conflictCategories.sink,
  },
  wallLocation: {
    actionType: constants.SELECT_WALL_LOCATION,
    message: parameterMessages.wallLocation,
    selected: parameters.wallLocationParameterSelector,
    available: availableOptions.availableWallLocationsSelector,
    category: conflictCategories.plan,
  },
  shape: {
    actionType: constants.CHANGE_SHAPE,
    message: parameterMessages.shape,
    selected: parameters.plannedShapeParameterSelector,
    available: availableOptions.availableShapesSelector,
    category: conflictCategories.plan,
  },
  overhang: {
    actionType: constants.SELECT_OVERHANG,
    message: parameterMessages.overhangType,
    selected: parameters.overhangParameterSelector,
    available: availableOptions.availableOverhangsSelector,
    category: conflictCategories.plan,
  },
  overhangSide: {
    actionType: constants.SELECT_OVERHANG_SIDE,
    message: parameterMessages.overhangSide,
    selected: parameters.overhangSideParameterSelector,
    available: availableOptions.availableOverhangSidesSelector,
    autoResolve: true,
    category: conflictCategories.plan,
  },
  splashback: {
    actionType: constants.SELECT_SPLASHBACK,
    message: parameterMessages.splashback,
    selected: parameters.splashbackParameterSelector,
    available: availableOptions.availableSplashbacksSelector,
    category: conflictCategories.style,
  },
  kitchenIslandSplashback: {
    actionType: constants.SELECT_KITCHEN_ISLAND_SPLASH_BACK,
    message: parameterMessages.kitchenIslandSplashback,
    selected: parameters.splashbackParameterSelector,
    available: availableOptions.availableKitchenIslandSplashbacksSelector,
    category: conflictCategories.kitchenIsland,
  },
  wallPanelMaterial: {
    actionType: constants.SELECT_WALL_PANEL_MATERIAL,
    message: parameterMessages.wallPanelMaterial,
    selected: parameters.wallPanelMaterialParameterSelector,
    available: availableOptions.availableWallPanelMaterialsSelector,
    category: conflictCategories.style,
  },
  kitchenIslandWallLocation: {
    actionType: constants.SELECT_KITCHEN_ISLAND_WALL_LOCATION,
    message: parameterMessages.kitchenIslandWallLocation,
    selected: parameters.kitchenIslandWallLocationParameterSelector,
    available: availableOptions.availableKitchenIslandWallLocationsSelector,
    category: conflictCategories.kitchenIsland,
  },
  wallPanelExpression: {
    actionType: constants.SELECT_WALL_PANEL_EXPRESSION,
    message: parameterMessages.wallPanelExpression,
    selected: parameters.wallPanelExpressionParameterSelector,
    available: availableOptions.availableWallPanelExpressionsSelector,
    category: conflictCategories.style,
  },
  kitchenIslandMaterial: {
    actionType: constants.SELECT_KITCHEN_ISLAND_MATERIAL,
    message: parameterMessages.kitchenIslandMaterial,
    selected: parameters.kitchenIslandMaterialParameterSelector,
    available: availableOptions.availableKitchenIslandMaterialsSelector,
    category: conflictCategories.kitchenIsland,
  },
  kitchenIslandExpressions: {
    actionType: constants.SELECT_KITCHEN_ISLAND_EXPRESSION,
    message: parameterMessages.kitchenIslandExpression,
    selected: parameters.kitchenIslandExpressionParameterSelector,
    available: availableOptions.availableKitchenIslandExpressionsSelector,
    category: conflictCategories.kitchenIsland,
  },
  kitchenIslandEdge: {
    actionType: constants.SELECT_KITCHEN_ISLAND_EDGE,
    message: parameterMessages.kitchenIslandEdge,
    selected: parameters.kitchenIslandEdgeParameterSelector,
    available: availableOptions.availableKitchenIslandEdgesSelector,
    autoResolve: true,
    category: conflictCategories.kitchenIsland,
  },
  kitchenIslandThickness: {
    actionType: constants.SELECT_KITCHEN_ISLAND_THICKNESS,
    message: parameterMessages.kitchenIslandThickness,
    selected: parameters.kitchenIslandThicknessParameterSelector,
    available: availableOptions.availableKitchenIslandThicknessesSelector,
    autoResolve: true,
    category: conflictCategories.kitchenIsland,
  },
  kitchenIslandOverhang: {
    actionType: constants.SELECT_KITCHEN_ISLAND_OVERHANG,
    message: parameterMessages.kitchenIslandOverhang,
    selected: parameters.kitchenIslandOverhangParameterSelector,
    available: availableOptions.availableKitchenIslandOverhangsSelector,
    category: conflictCategories.kitchenIsland,
  },
  kitchenIslandOverhangSide: {
    actionType: constants.SELECT_KITCHEN_ISLAND_OVERHANG_SIDE,
    message: parameterMessages.kitchenIslandOverhangSide,
    selected: parameters.kitchenIslandOverhangSideParameterSelector,
    available: availableOptions.availableKitchenIslandOverhangSidesSelector,
    autoResolve: true,
    category: conflictCategories.kitchenIsland,
  },
};

export const wpMaterialCanMatchSelector = createSelector(
  parameters.materialParameterSelector,
  availableOptions.availableWallPanelMaterialsSelector,
  (material, available) => {
    const materialId = material.getIn(['value', 'id']);
    return !!available.find((o) => o.get('id') === materialId);
  }
);

export const wpExpressionCanMatchSelector = createSelector(
  wpMaterialCanMatchSelector,
  parameters.materialParameterSelector,
  availableOptions.availableWallPanelMaterialsSelector,
  parameters.expressionParameterSelector,
  (canMatchMaterial, materialParam, availableMaterial, expressionParam) => {
    if (!canMatchMaterial) {
      return false;
    }

    const materialId = materialParam.getIn(['value', 'id']);
    const expressionId = expressionParam.getIn(['value', 'id']);
    return !!availableMaterial
      .find((o) => o.get('id') === materialId)
      .get('expressions')
      .find((e) => e.get('id') === expressionId);
  }
);

const matchInvalid = (canMatchSelector) => createSelector(
  parameters.isMaterialUsedSelector,
  parameters.splashbackParameterSelector,
  wallPanelMatchWorktopSelector,
  canMatchSelector,
  (isMaterialUsed, splashback, shouldMatch, canMatch) => {
    if (isMaterialUsed) {
      return splashback.getIn(['value', 'id']) === WALL_PANEL_ID && shouldMatch && !canMatch
    } else {
      return false;
    }
  }
);

export const automaticParametersSelector = createSelector(
  planSelector,
  createFieldSelector(parameterWithConflictHandling, 'selected'),
  createFieldSelector(allOperationParameters, 'parameter'),
  (plan, selectedParameters, selectedOperations) => {
    const lastAction = plan.get('lastAction');

    const automaticParameters = selectedParameters.map((param) => addAutomatic(lastAction, param.actionType, param.message, param.selected, param.category));
    const operations = selectedOperations.map((param) => addAutomatic(lastAction, param.actionType, param.message, param.parameter, param.category));

    return List(automaticParameters)
      .concat(operations)
      .filter((defined) => defined);
  }
);

export const conflictingParametersSelector = createSelector(
  parameters.isMaterialUsedSelector,
  configurationSelector,
  planSelector,
  parameters.plannedShapeParameterSelector,
  shapeInvalidSelector,
  wallPanelHeightInvalidSelector,
  wallEdgingStripCustomizableHeightInvalidSelector,
  kitchenIslandInvalidSelector,
  overhangInvalidSelector,
  freestandingHobInvalidSelector,
  kitchenIslandOverhangInvalidSelector,
  createStructuredSelector(_.mapValues(parameterWithConflictHandling, (param) => selectedFromAvailableSelector(param.selected, param.available))),
  createFieldSelector(allOperationParameters, 'conflicts', 'parameter'),
  matchInvalid(wpMaterialCanMatchSelector),
  matchInvalid(wpExpressionCanMatchSelector),
  (isMaterialUsed, configuration, plan, plannedShape, shapeInvalid, wallPanelHeightInvalid, wallEdgingStripCustomizableHeightInvalid, kitchenIslandInvalid, overhangInvalid, freestandingHobInvalid, kitchenIslandOverhangInvalid, structured, operations, wpMaterialInvalidMatch, wpExprInvalidMatch) => {
    const lastAction = plan.get('lastAction');
    return List(_.uniqBy(_.toPairs(parameterWithConflictHandling)
      .map(([id, param]) => addConflicts(plan, configuration, lastAction, param.actionType, param.message, structured[id], param.autoResolve, param.category))
      .concat(operations.map((operation) => {
        if (operation.conflicts && operation.parameter.get('value') > 0) {
          return { message: operation.message, lastAction: lastAction === operation.actionType, autoResolve: true, category: operation.category };
        }
        return null;
      }))
      .concat(shapeInvalid ? { message: parameterMessages.shape, lastAction: lastAction === parameterWithConflictHandling.shape.actionType, ignore: true, category: isMaterialUsed ? conflictCategories.plan : conflictCategories.style } : null)
      .concat(wallPanelHeightInvalid ? { message: parameterMessages.splashback, lastAction: false, ignore: true, category: conflictCategories.style } : null)
      .concat(wallEdgingStripCustomizableHeightInvalid ? { message: parameterMessages.splashback, lastAction: false, ignore: true, category: conflictCategories.style } : null)
      .concat(kitchenIslandInvalid ? { message: parameterMessages.kitchenIsland, lastAction: false, ignore: true, category: conflictCategories.kitchenIsland } : null)
      .concat(kitchenIslandOverhangInvalid ? { message: parameterMessages.kitchenIslandOverhangSide, lastAction: false, ignore: true, category: conflictCategories.kitchenIsland } : null)
      .concat(overhangInvalid ? { message: parameterMessages.overhangSide, lastAction: false, ignore: true, category: conflictCategories.plan } : null)
      .concat(freestandingHobInvalid ? { message: parameterMessages.freestandingHob, lastAction: false, ignore: true, category: conflictCategories.cutouts } : null)
      .concat(wpMaterialInvalidMatch ? { message: parameterMessages.wallPanelMaterial, lastAction: false, category: conflictCategories.style } : null)
      .concat(wpExprInvalidMatch ? { message: parameterMessages.wallPanelExpression, lastAction: false, category: conflictCategories.style } : null)
      .filter((defined) => defined), 'message'));
  }
);

export const conflictingAndAutomaticParametersSelector = createSelector(
  automaticParametersSelector,
  conflictingParametersSelector,
  (automaticParameters, conflictingParameters) => {
    return automaticParameters.concat(conflictingParameters);
  }
);

export const planHasUnavailableOptions = (plan) => plan.some((param, index) => {
  if (index!== undefined && index !==null && (index === 'wallPanelSingleCut' || index === 'wallPanelMaterial' || index === 'wallPanelTripleCut' || index === 'wallPanelSquareCut' || index === 'splashback' || index === 'wallPanelExpression' || index === 'wallPanelDoubleCut' || index === 'wallPanelMatchWorktop' || index === 'wallPanelRoundCutSmall')) {
    return false;
  }
  if (index!== undefined && index !==null && plan.getIn(['material', 'value', 'id']) === 'noMaterial' && typeof param === 'object' && param.get('value') === null && (index === 'expression')) {
    return false;
  }
  if (index!== undefined && index !==null && plan.getIn(['material', 'value', 'edges']) === 'noEdgeIncluded' && typeof param === 'object' && param.get('value') === null && (index === 'edge')) {
    return false;
  }
  if (index!== undefined && index !==null && plan.getIn(['kitchenIslandMaterial', 'value', 'edges']) === 'noEdgeIncluded' && typeof param === 'object' && param.get('value') === null && (index === 'kitchenIslandEdge')) {
    return false;
  }
  if (index!== undefined && index !==null && (plan.getIn(['coverPanel', 'value']) === null || plan.getIn(['coverPanel', 'value'])["size"] === 0) && typeof param === 'object' && param.get('value') === null && (index === 'coverPanel')) {
    return false;
  }
  if (index!== undefined && index !==null && (plan.getIn(['kitchenIslandCoverPanel', 'value']) === null || plan.getIn(['kitchenIslandCoverPanel', 'value'])["size"] === 0) && typeof param === 'object' && param.get('value') === null && (index === 'kitchenIslandCoverPanel')) {
    return false;
  }

  return typeof param === 'object' && param.get('value') === null;
});

export const planHasUnavailableOptionsSelector = createSelector(
  planSelector,
  planHasUnavailableOptions,
);

const hasConflict = (parameter, available, lastAction, action) => {
  if (available) {
    if (available.size === 0) {
      return false;
    }
  }
  const option = available.find((o) => o.get('id') === parameter.getIn(['value', 'id']));

  if (option.get('conflicts') && lastAction === "app/Options/SELECT_MATERIAL" && action === "app/Options/SELECT_KITCHEN_ISLAND_THICKNESS") {
    return false;
  }
  return !option || (option.get('conflicts') && option.get('conflicts').size > 0) || false;
};

const tryResolve = (lastAction, action, parameter, available) => {
  const inConflict = hasConflict(parameter, available, lastAction, action);
  if (lastAction !== action) {
    const nonConflicting = available.filterNot((opt) => opt.get('conflicts'));
    if (inConflict) {
      if (nonConflicting.size > 0 && lastAction === constants.SELECT_SINK_MODEL && action === constants.SELECT_THICKNESS) {
        return {
          inConflict: false,
          value: fromJS({
            value: nonConflicting.get(0),
            selectionType: SELECTION_TYPE_AUTOMATIC,
          }),
        };
      }
      if (nonConflicting.size === 1) {
        return {
          inConflict: false,
          value: fromJS({
            value: nonConflicting.get(0),
            selectionType: parameter.get('selectionType') === SELECTION_TYPE_USER ? SELECTION_TYPE_AUTOMATIC : SELECTION_TYPE_DEFAULT,
          }),
        };
      } else if (nonConflicting.size > 0 && parameter.get('selectionType') === SELECTION_TYPE_DEFAULT) {
        return {
          inConflict: false,
          value: fromJS({
            value: nonConflicting.get(0),
            selectionType: SELECTION_TYPE_DEFAULT,
          }),
        };
      }
    } else if (nonConflicting.get(0) !== parameter.get('value') && parameter.get('selectionType') === SELECTION_TYPE_DEFAULT) {
      return {
        inConflict: false,
        value: fromJS({
          value: nonConflicting.get(0),
          selectionType: SELECTION_TYPE_DEFAULT,
        }),
      };
    }
  }

  return {
    inConflict,
    value: parameter,
  };
};

const tryResolveOperation = (lastAction, action, parameter, conflicts) => {
  const inConflict = parameter.get('value') > 0 && conflicts;
  if (lastAction !== action) {
    if (inConflict) {
      return {
        inConflict: false,
        value: fromJS({
          selectionType: parameter.get('selectionType') === SELECTION_TYPE_USER ? SELECTION_TYPE_AUTOMATIC : SELECTION_TYPE_DEFAULT,
          value: 0,
        }),
      };
    }
  }

  return {
    inConflict,
    value: parameter,
  };
};

export const automaticPlanSelector = createSelector(
  planHasUnavailableOptionsSelector,
  conflictingParametersSelector,
  planSelector,
  createFieldSelector(_.map(parameterWithConflictHandling, (v, id) => ({ ...v, id })), 'selected', 'available'),
  createFieldSelector(allOperationParameters, 'parameter', 'conflicts'),
  (hasUnavailableOptions, conflicts, plan, selectedParams, operations) => {
    if (hasUnavailableOptions) {
      return plan;
    }

    if (plan.get('skipAutomaticResolve')) {
      const importantConflicts = conflicts.filterNot((c) => c.ignore);
      if (importantConflicts.size === 0) {
        return plan.set('skipAutomaticResolve', false);
      } else if (importantConflicts.find(({ autoResolve }) => !autoResolve)) {
        return plan;
      }
    }

    const lastAction = plan.get('lastAction');

    return selectedParams
      .filter((param) => param.autoResolve)
      .map((param) => [param.id, tryResolve(lastAction, param.actionType, param.selected, param.available)])
      .concat(
        operations.map((param) => [param.operation, tryResolveOperation(lastAction, param.actionType, param.parameter, param.conflicts)])
      )
      .reduce((prev, [id, resolved]) => {
        const modified = prev.setIn([id, 'value'], resolved.value.get('value')).setIn([id, 'selectionType'], resolved.value.get('selectionType'));

        if (resolved.inConflict) {
          return modified.set('skipAutomaticResolve', true);
        }

        return modified;
      }, plan);
  }
);
